import '../polyfills';
import '../assets/global.css';
import '@ui/utils/bigint';

import { Analytics } from '@vercel/analytics/next';
import { PrivyProvider } from '@privy-io/react-auth';
import { Query } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { IconPreloader } from '@ui/components/IconPreloader';
import { VConsole } from '@ui/components/VConsole';
import { WalletModalProvider } from '@ui/components/WalletModal/WalletModalProvider';
import { WormholeWrapper } from '@ui/components/Wormhole/wormholeWrapper';
import { BrandId } from '@ui/config/brand';
import { BrandProvider, useBrand } from '@ui/config/BrandProvider';
import { BrandThemeStyle } from '@ui/config/BrandThemeStyle';
import { intel, orbitron, plexMono, righteous } from '@ui/constants/fonts';
import { getMeta } from '@ui/constants/meta';
import { ROUTES } from '@ui/constants/routes';
import { useAntiDdosConfigInit } from '@ui/features/OneClickTrading/hooks/useAntiDdosConfigInit';
import { OneClickTradingModal } from '@ui/features/OneClickTrading/OneClickTradingModal';
import { PrivacyPoolsAccountProvider } from '@ui/features/privacy-pools/account/state/PrivacyPoolsAccountProvider';
import { useReferralCodeHandler } from '@ui/features/Referral/hooks/useReferralCodeHandler';
import { ReferralCodeModal } from '@ui/features/Referral/ReferralCodeModal';
import { ScrollLockWatchdog } from '@ui/components/ScrollLockWatchdog';
import { V2FeedbackWidget } from '@ui/features/V2Feedback/V2FeedbackWidget';
import { VipModal } from '@ui/features/Vip/VipModal';
import {
  useInitPairMarketScheduleWorker,
  useSyncBackendConfig,
} from '@ui/features/RWA/hooks/usePairMarketScheduleStore/usePairMarketScheduleStore';
import { useSyncDataChainStore } from '@ui/hooks/datachain';
import {
  useInitPriceLiveStore,
  usePriceLiveStore,
  useRegisterPriceLiveStore,
} from '@ui/hooks/price/usePriceLive';
import { useTokenDisplayDecimals } from '@ui/hooks/useTokenDisplayDecimals';
import { useTradingPair } from '@ui/features/Trade/store/useTradingPair';
import { Layout } from '@ui/layouts/Layout';
import { useBugsnagMeta, WithErrorBoundary } from '@ui/lib/bugsnag';
import { getPosthog, useIdentifyPostHogUserOnConnect } from '@ui/lib/posthog';
import { createIDBPersister, queryClient } from '@ui/lib/reactQuery';
import { TimerProvider } from '@ui/lib/timer/timerCtx';
import { config as wagmiConfig } from '@ui/lib/wallet';
import { useReconnectStream } from '@ui/services/pyth-benchmark/stream/useReconnectStream';
import { NextPage } from 'next';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import BigNumber from 'bignumber.js';
import { useEffect } from 'react';
import { appWithTranslation, useTranslation } from 'next-i18next';
import { GoogleAnalytics } from 'nextjs-google-analytics';
import NextProgress from 'nextjs-progressbar';
import { css } from 'styled-system/css';
import { Box } from 'styled-system/jsx';
import { WagmiProvider } from 'wagmi';

import nextI18NextConfig from '../../next-i18next.config.js';

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
};

export type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout<any>;
  brandId?: BrandId;
};

// const LazyPosthog = dynamic(
//   () => import('@ui/lib/posthog').then((v) => v.PostHogCustomizedProvider),
//   { ssr: true },
// );
const LazyBasicToast = dynamic(
  () => import('@ui/components/Toast/BasicToast').then((v) => v.BasicToast),
  // ssr:false — the Ark UI toaster hydrates with a different data-scope than it
  // server-renders (dialog vs toast), which intermittently crashes hydration
  { ssr: false },
);
// const LazyTgeOptInPopup = dynamic(
//   () =>
//     import(
//       '@ui/features/TGE/components/PreSaleOptInPopup/PreSaleOptInPopup'
//     ).then((v) => v.PreSaleOptInPopup),
//   { ssr: true },
// );

// getPosthog();

// Some of the hooks are required to be called under certain context.
// We extract the MyPage component as a child component to make sure that
// we have access to all the contexts in side MyPage component.
const MyPage = ({ Component, ...restProps }: AppPropsWithLayout) => {
  useSyncDataChainStore();

  const getLayout = Component.getLayout ?? ((page) => page);
  const rendered = getLayout(<Component {...restProps} />);

  return (
    <>
      <AppHead route={restProps.router.pathname as ROUTES} />
      <BrandThemeStyle />
      <GoogleAnalytics
        trackPageViews
        gaMeasurementId={process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID}
      />
      <NextProgress />
      <GlobalComponents />
      <Box
        className={`${intel.variable} ${orbitron.variable} ${plexMono.variable} ${righteous.variable}`}
      >
        <Layout>{rendered}</Layout>
      </Box>
    </>
  );
};

function MyApp(appProps: AppPropsWithLayout) {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: createIDBPersister(),
        maxAge: 1000 * 60 * 60 * 2, // 2 hours
        hydrateOptions: {},
        dehydrateOptions: {
          shouldDehydrateQuery: (q: Query) => {
            if (q?.meta?.persist != true) return false;
            if (q.state.status !== 'success') return false;
            try {
              const s = JSON.stringify(q.state.data);
              if (s && s.length > 200 * 1024) return false;
            } catch {
              // 无法 JSON.stringify 就不持久化
              return false;
            }

            return true;
          },
        },
      }}
    >
      {/* <PrivyProvider
        appId="cmh4glz9y02qfkz0d25fp03at"
        config={{
          embeddedWallets: {
            ethereum: {
              createOnLogin: 'users-without-wallets',
            },
          },
        }}
      > */}
      <BrandProvider brandId={appProps.brandId}>
        <WagmiProvider config={wagmiConfig}>
          <WithErrorBoundary>
            {/* <LazyPosthog> */}
            <TimerProvider>
              <PrivacyPoolsAccountProvider>
                <MyPage {...appProps} />
              </PrivacyPoolsAccountProvider>
            </TimerProvider>
            <VConsole />
            <Box
              className={css({
                '& .tsqd-open-btn-container': {
                  position: 'fixed',
                  left: '-32px!important',
                  cursor: 'move',
                  transition: 'left 0.2s ease',
                  _hover: {
                    left: '0!important',
                  },
                },
              })}
            >
              {/* {process.env.NODE_ENV !== 'production' && (
                  <ReactQueryDevtools
                    initialIsOpen={false}
                    buttonPosition="bottom-left"
                  />
                )} */}
            </Box>
            {/* </LazyPosthog> */}
            <GlobalHooks />
          </WithErrorBoundary>
        </WagmiProvider>
      </BrandProvider>
      {/* </PrivyProvider> */}
      <Analytics />
    </PersistQueryClientProvider>
  );
}

function GlobalComponents() {
  return (
    <WalletModalProvider>
      <LazyBasicToast />
      {/* <LazyTgeOptInPopup /> */}
      <IconPreloader />
      <ReferralCodeModal />
      <VipModal />
      <OneClickTradingModal />
      <WormholeWrapper />
      {/* TEMPORARY design-review tool, no-op unless NEXT_PUBLIC_V2_FEEDBACK=1 — remove before launch */}
      <ScrollLockWatchdog />
      <V2FeedbackWidget />
    </WalletModalProvider>
  );
}
function GlobalHooks() {
  useBugsnagMeta();
  useIdentifyPostHogUserOnConnect();
  useRegisterPriceLiveStore();
  useReconnectStream();
  useInitPriceLiveStore();
  useSyncBackendConfig();
  useInitPairMarketScheduleWorker();
  useReferralCodeHandler();
  useAntiDdosConfigInit();

  return <></>;
}

/**
 * Dynamically update document.title with the current trading pair name
 * and live price when the user is on the trade page.
 */
function DynamicTradeTitle({ baseTitle }: { baseTitle: string }) {
  const tradingPair = useTradingPair((s) => s.useTradingPair());
  const baseSymbol = tradingPair?.baseSymbol;
  const price = usePriceLiveStore((s) => {
    if (!baseSymbol) return undefined;
    const record = s.priceRecord;
    if (!record) return undefined;
    return record[baseSymbol as keyof typeof record];
  }) as BigNumber | undefined;
  const priceDisplayDecimals = useTokenDisplayDecimals(
    baseSymbol,
    tradingPair?.priceDisplayDecimals,
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (price?.isFinite()) {
      const formattedPrice = `$${price.toFormat(priceDisplayDecimals)}`;
      document.title = `${formattedPrice} | ${tradingPair?.pairName ?? 'Trade'} | ${baseTitle}`;
    } else {
      document.title = `${tradingPair?.pairName ?? 'Trade'} | ${baseTitle}`;
    }
  }, [tradingPair, price, priceDisplayDecimals, baseTitle]);

  return null;
}

function AppHead({ route }: { route: ROUTES }) {
  const { t } = useTranslation();
  const brand = useBrand();
  const {
    canonical,
    card,
    description,
    ogImageUrl,
    siteName,
    title,
    twitterImage,
    twitterSite,
    type,
  } = getMeta(t, route, brand);

  return (
    <>
      <Head>
        <title>{title}</title>

        <meta name="title" content={title} />
        <meta name="description" content={description} />

        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:card" content={card} />
        <meta name="twitter:image" content={twitterImage} />
        <meta name="twitter:site" content={twitterSite} />

        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content={type} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:site_name" content={siteName} />
        <meta property="og:url" content={canonical} />

        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="x-default" href={canonical} />

        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0"
        />
      </Head>
      {route === ROUTES.INDEX && (
        <DynamicTradeTitle baseTitle={brand.title} />
      )}
    </>
  );
}

export default appWithTranslation(MyApp, nextI18NextConfig);
